// src/pages/Products/ProductOrderManager.js
import { useState, useEffect } from "react";
import { collection, getDocs, doc, writeBatch, updateDoc } from "firebase/firestore";
import { db } from "../../../db/firebase";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUp, ArrowDown, Save, ArrowLeft, GripVertical } from "lucide-react";

export default function ProductOrderManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [organizedCategories, setOrganizedCategories] = useState([]);
  
  const navigate = useNavigate();

  // Fetch products and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch products
        const productsSnapshot = await getDocs(collection(db, "products"));
        const productsData = productsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        
        // Sort by current order
        const sortedProducts = productsData.sort((a, b) => {
          const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
          const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        
        setProducts(sortedProducts);

        // Fetch categories for display
        const categoriesSnapshot = await getDocs(collection(db, "categories"));
        const categoriesData = categoriesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        
        // Organize categories
        const organized = organizeCategoriesHierarchy(categoriesData);
        setOrganizedCategories(organized);
        
      } catch (error) {
        console.error("Error fetching data:", error);
        alert("Error loading products. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Function to organize categories into hierarchy
  const organizeCategoriesHierarchy = (categoriesData) => {
    const mainCategories = categoriesData.filter(cat => !cat.ParentCategory);
    const subCategories = categoriesData.filter(cat => cat.ParentCategory);

    const categoriesWithChildren = mainCategories.map(mainCat => ({
      ...mainCat,
      subcategories: subCategories.filter(subCat => subCat.ParentCategory === mainCat.id)
    }));

    return categoriesWithChildren;
  };

  // Function to get category names for a product
  const getCategoryNames = (product) => {
    if (!product.PCategories || !organizedCategories.length) return ["No categories"];
    
    const categoryNames = [];
    
    organizedCategories.forEach(mainCat => {
      // Check main category
      if (product.PCategories.includes(mainCat.id)) {
        categoryNames.push(mainCat.CategoryName);
      }
      
      // Check subcategories
      if (mainCat.subcategories) {
        mainCat.subcategories.forEach(subCat => {
          if (product.PCategories.includes(subCat.id)) {
            categoryNames.push(`${mainCat.CategoryName} → ${subCat.CategoryName}`);
          }
        });
      }
    });

    return categoryNames.length > 0 ? categoryNames : ["No categories"];
  };

  // Move product up in order
  const moveUp = (index) => {
    if (index === 0) return; // Can't move first item up
    
    const newProducts = [...products];
    const currentProduct = newProducts[index];
    const previousProduct = newProducts[index - 1];
    
    // Swap order numbers
    const tempOrder = currentProduct.order;
    currentProduct.order = previousProduct.order;
    previousProduct.order = tempOrder;
    
    // Swap positions in array
    newProducts[index] = previousProduct;
    newProducts[index - 1] = currentProduct;
    
    setProducts(newProducts);
    setHasChanges(true);
  };

  // Move product down in order
  const moveDown = (index) => {
    if (index === products.length - 1) return; // Can't move last item down
    
    const newProducts = [...products];
    const currentProduct = newProducts[index];
    const nextProduct = newProducts[index + 1];
    
    // Swap order numbers
    const tempOrder = currentProduct.order;
    currentProduct.order = nextProduct.order;
    nextProduct.order = tempOrder;
    
    // Swap positions in array
    newProducts[index] = nextProduct;
    newProducts[index + 1] = currentProduct;
    
    setProducts(newProducts);
    setHasChanges(true);
  };

  // Update order numbers sequentially
  const reorderSequentially = () => {
    const newProducts = products.map((product, index) => ({
      ...product,
      order: index + 1
    }));
    
    setProducts(newProducts);
    setHasChanges(true);
  };

  // Save changes to Firestore
  const saveChanges = async () => {
    try {
      setSaving(true);
      
      // Use batch for better performance
      const batch = writeBatch(db);
      
      products.forEach(product => {
        const productRef = doc(db, "products", product.id);
        batch.update(productRef, { order: product.order });
      });
      
      await batch.commit();
      
      setHasChanges(false);
      alert("✅ Product order updated successfully!");
      
    } catch (error) {
      console.error("Error updating order:", error);
      alert("❌ Error updating product order. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle drag and drop
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index.toString());
    e.currentTarget.classList.add("opacity-50");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("bg-blue-50");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("bg-blue-50");
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.currentTarget.classList.remove("bg-blue-50");
    
    const sourceIndex = parseInt(e.dataTransfer.getData("text/plain"));
    
    if (sourceIndex === targetIndex) return;
    
    const newProducts = [...products];
    const [movedProduct] = newProducts.splice(sourceIndex, 1);
    newProducts.splice(targetIndex, 0, movedProduct);
    
    // Update order numbers based on new positions
    const reorderedProducts = newProducts.map((product, index) => ({
      ...product,
      order: index + 1
    }));
    
    setProducts(reorderedProducts);
    setHasChanges(true);
  };

  // Reset to original order
  const resetOrder = async () => {
    if (!window.confirm("Are you sure you want to reset all changes? This will revert to the last saved order.")) {
      return;
    }
    
    try {
      setLoading(true);
      const productsSnapshot = await getDocs(collection(db, "products"));
      const productsData = productsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      const sortedProducts = productsData.sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
      
      setProducts(sortedProducts);
      setHasChanges(false);
      
    } catch (error) {
      console.error("Error resetting order:", error);
      alert("Error resetting order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/admin/products"
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition font-medium flex items-center gap-2"
          >
            <ArrowLeft size={18} />
            Back to Products
          </Link>
          <h2 className="text-2xl font-bold text-purple-800">Product Order Manager</h2>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/products"
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition font-medium flex items-center gap-2"
          >
            <ArrowLeft size={18} />
            Back to Products
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-purple-800">Product Order Manager</h2>
            <p className="text-gray-600 mt-1">
              Drag and drop or use arrows to reorder products. Changes are saved when you click "Save Order".
            </p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={reorderSequentially}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2"
          >
            Renumber All
          </button>
          
          {hasChanges && (
            <button
              onClick={resetOrder}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition font-medium"
            >
              Reset
            </button>
          )}
          
          <button
            onClick={saveChanges}
            disabled={!hasChanges || saving}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={18} />
            {saving ? "Saving..." : "Save Order"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">Total Products</div>
          <div className="text-2xl font-bold text-gray-900">{products.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">With Order Numbers</div>
          <div className="text-2xl font-bold text-purple-600">
            {products.filter(p => p.order !== undefined).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">Current Range</div>
          <div className="text-2xl font-bold text-blue-600">
            {products.length > 0 ? `1 - ${products.length}` : '0'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-sm font-medium text-gray-500">Status</div>
          <div className={`text-lg font-bold ${hasChanges ? 'text-orange-600' : 'text-green-600'}`}>
            {hasChanges ? 'Unsaved Changes' : 'All Saved'}
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-4 border-b font-semibold text-gray-700 w-20">Order</th>
              <th className="p-4 border-b font-semibold text-gray-700 w-12">Move</th>
              <th className="p-4 border-b font-semibold text-gray-700">Product Name</th>
              <th className="p-4 border-b font-semibold text-gray-700">Categories</th>
              <th className="p-4 border-b font-semibold text-gray-700 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length > 0 ? (
              products.map((product, index) => {
                const categoryNames = getCategoryNames(product);
                const isFirst = index === 0;
                const isLast = index === products.length - 1;
                
                return (
                  <tr 
                    key={product.id}
                    className="border-b hover:bg-gray-50 transition-colors"
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={(e) => e.currentTarget.classList.remove("opacity-50")}
                  >
                    {/* Order Number */}
                    <td className="p-4 border-b">
                      <div className="flex items-center justify-center">
                        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium border border-purple-200">
                          {product.order || "Not set"}
                        </span>
                      </div>
                    </td>
                    
                    {/* Drag Handle */}
                    <td className="p-4 border-b">
                      <div className="flex justify-center">
                        <GripVertical 
                          size={18} 
                          className="text-gray-400 cursor-grab hover:text-gray-600"
                        />
                      </div>
                    </td>
                    
                    {/* Product Name */}
                    <td className="p-4 border-b">
                      <div className="flex items-center space-x-3">
                        {product.PType?.[0]?.PImgUrls?.[0] && (
                          <img 
                            src={product.PType[0].PImgUrls[0]} 
                            alt={product.PName}
                            className="w-10 h-10 object-cover rounded border"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">
                            {product.PName || "Unnamed Product"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {product.PCode || "No SKU"}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Categories */}
                    <td className="p-4 border-b">
                      <div className="flex flex-wrap gap-1">
                        {categoryNames.slice(0, 2).map((categoryName, catIndex) => (
                          <span 
                            key={catIndex}
                            className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs"
                          >
                            {categoryName}
                          </span>
                        ))}
                        {categoryNames.length > 2 && (
                          <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                            +{categoryNames.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    
                    {/* Actions */}
                    <td className="p-4 border-b">
                      <div className="flex space-x-2 justify-center">
                        <button
                          onClick={() => moveUp(index)}
                          disabled={isFirst}
                          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 transition disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move Up"
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          onClick={() => moveDown(index)}
                          disabled={isLast}
                          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 transition disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move Down"
                        >
                          <ArrowDown size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="p-8 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m8-8V4a1 1 0 00-1-1h-2a1 1 0 00-1 1v1M9 7h6" />
                    </svg>
                    <p className="text-lg font-medium mb-2">No products found</p>
                    <p className="text-sm mb-4">
                      Add some products first to manage their order
                    </p>
                    <Link
                      to="/admin/products/new"
                      className="bg-purple-700 px-4 py-2 text-white rounded hover:bg-purple-800 transition font-medium"
                    >
                      + Add New Product
                    </Link>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">How to reorder products:</h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li><strong>Drag & Drop:</strong> Use the grip icon to drag products to new positions</li>
          <li><strong>Arrow Buttons:</strong> Use up/down arrows to move products one position at a time</li>
          <li><strong>Renumber All:</strong> Click "Renumber All" to reset order numbers sequentially from 1</li>
          <li><strong>Save:</strong> Don't forget to click "Save Order" to apply changes to the database</li>
        </ul>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 bg-orange-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
          <span className="font-medium">You have unsaved changes</span>
          <button
            onClick={saveChanges}
            disabled={saving}
            className="bg-white text-orange-600 px-3 py-1 rounded text-sm font-medium hover:bg-gray-100 transition disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Now"}
          </button>
        </div>
      )}
    </div>
  );
}